import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import { useFormState } from "../../../functions/useFormState";
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import {
  selectUserDropdowns,
  selectDropdownOptions,
} from "../../../redux/slices/dropdownSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import { setGeneralDetails } from "../../../redux/slices/psnSlice";
import { selectJobDetailsSection, selectFieldDescriptions } from "../../../redux/selectors";
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice"; // Import the selector
import useFieldClass from "../../../functions/useFieldClass";
import { selectGlobalVariables } from "../../../redux/slices/globalVariablesSlice"; // Import the selector for global variables

function JobGeneralProjectInfoRF({ zone }) {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails); // User details
  const userRoles = useSelector(selectUserRoles); // User details
  const currentUser = userDetails?.name;
  const apiUrls = useSelector(selectApiUrls); // Fetch API URLs from Redux
  const apiUrl = apiUrls.generalProject;
  const sectionName = "generalProject";
  const tableKey = sectionName;

  const options = useSelector(selectDropdownOptions); // Fetch dropdown values from redux
  const userDropdowns = useSelector(selectUserDropdowns);
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux

  const GeneraljobDetails = useSelector((state) =>
    selectJobDetailsSection(state, sectionName)
  );

  const globalVariables = useSelector(selectGlobalVariables); // Fetch global variables

  const releaseGuidelinesUrl = useMemo(() => {
    const releaseGuidelinesVariable = globalVariables.find(
      (variable) => variable.Title === "Apps Release to Engineering Guidelines"
    );
    return releaseGuidelinesVariable?.VariableValue || "";
  }, [globalVariables]);
  
  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly;

  const [roles, setRoles] = useState(mapUserRoles(userRoles));

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);
  // useEffect(() => {
  //   dispatch(clearFieldDescriptions()); // Clear field descriptions on component mount
  // }, [dispatch]);

  // useEffect(() => {
  //   dispatch(fetchFieldDescriptions()); // Fetch field descriptions on component mount
  // }, [dispatch]);
  
const fieldDescriptions = useSelector(selectFieldDescriptions);

    const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
    const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const jobDetails = useMemo(
    () => GeneraljobDetails?.[0] || {},
    [GeneraljobDetails]
  );

  const { formData, handleChange, setFormData } = useFormState({});

  useEffect(() => {
    if (jobDetails) {
      dispatch(setGeneralDetails(jobDetails)); // Set job details for PSN Copy
      setFormData({
        ID: jobDetails.ID || "",
        jobNumber: jobDetails.JobNumber || "",
        projectName: jobDetails.ProjectName || "",
        salesRepName: jobDetails.SalesRep || "",        orderDate: jobDetails.OrderDate?.split("T")[0] || "",
        zone: jobDetails.Zones || "",
        modelNumber: jobDetails.ModelNumber || "",
        drawingStatus: jobDetails.DrawingStatus || "",
        projectStatus: jobDetails.ProjectStatus || "",
        requestedDeliveryDate:
          jobDetails.RequestedDeliveryDate?.split("T")[0] || "",
        dateShipped: jobDetails.ActualShipDate?.split("T")[0] || "",
        csoDueDate: jobDetails.CSODueDate?.split("T")[0] || "",
        releaseDate: jobDetails.ReleaseDate?.split("T")[0] || "",
        projectManager: jobDetails.ProjectManager || "",
        mechanicalEngineer: jobDetails.MechanicalEngineer || "",
        electricalEngineer: jobDetails.ElectricalEngineer || "",
        controlsEngineer: jobDetails.ControlsEngineer || "",
        newConstruction: jobDetails.NewConstruction,
        hasSubmittal: jobDetails.HasSubmittal,
        sendDocumentsToSales: jobDetails.SendDocumentsToSales,
        customControlsEngineering: jobDetails.CustomControlsEngineering,
        drawingType: jobDetails.DrawingType || "",
        drawingNumber: jobDetails.DrawingNumber || "",
        witnessTestRequired: jobDetails.WitnessTestRequired || "",
        releaseMeetingRequired: jobDetails.ReleaseMeetingRequired || "",
        testCriteria: jobDetails.TestCriteria || "",
        complianceCertificateRequired: jobDetails.ComplianceCertificateRequired || "",
        witnessTestComments: jobDetails.WitnessTestComments || "",
      });
    }
  }, [jobDetails, setFormData, dispatch]);

  

  const getFieldTitle = (fieldName) => {
    return fieldDescriptions.find(desc => desc.ColumnName === fieldName)?.Description || "";
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`} delay={{ show: 300, hide: 200 }}>
      {getFieldTitle(fieldName).split("\n").map((line, index) => (
        <span key={index}>
          {line}
          <br />
        </span>
      ))}
    </Tooltip>
  );




  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  const drawingStatusOptions =
    options
      ?.filter((option) => option.DropdownType === "Drawing Status")
      .sort((a, b) => a.SortOrder - b.SortOrder) || []; // Default to an empty array if undefined
  const projectStatusOptions =
    options
      ?.filter((option) => option.DropdownType === "Project Status")
      .sort((a, b) => a.SortOrder - b.SortOrder) || []; // Default to an empty array if undefined
  const zoneOptions =
    options
      ?.filter((option) => option.DropdownType === "Zones")
      .sort((a, b) => a.SortOrder - b.SortOrder) || []; // Default to an empty array if undefined

  const drawingTypeOptions =
    options
      ?.filter((option) => option.DropdownType === "Drawing Type")
      .sort((a, b) => a.SortOrder - b.SortOrder) || []; // Default to an empty array if undefined
      const genericOptions =
    options
      ?.filter((option) => option.DropdownType === "Generic Yes/ No")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];

  function handleSubmit(e) {
    e.preventDefault();
  }

  // Get field classes for all fields
  const fieldClasses = useFieldClass(formData, zone);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }


  return (
    <div>

      <div>
        <div>
          {showNotification && (
            <div
              className={`save-notification ${
                saveStatus.includes("Failed") ? "error" : ""
              }`}
            >
              {saveStatus}
            </div>
          )}
        </div>

        <Form
          onSubmit={handleSubmit}
          className="general-mobile-single-column general-project-info-form mt-4"
        >
          <Row className="mb-3 row-cols-1 row-cols-sm-2 row-cols-lg-4">
            <Col className="flex-shrink-1">
              <Form.Group controlId="jobNumber">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("JobNumber")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Job Number</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="jobNumber"
                   value={formData.jobNumber || ""}
                  onFocus={handleFocus} // Capture the current value on focus
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Job Number",
                      [], // You can pass any options here if needed
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "JobNumber",
                        tableKey,
                      }
                    )
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.jobNumber} // Use precomputed class
                  onChange={handleChange}
                  placeholder="Enter Job Number"
                />
              </Form.Group>
            </Col>
            <Col className="flex-grow-3">
              <Form.Group controlId="projectName">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("ProjectName")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Project Name</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="projectName"
                  value={formData.projectName}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Project Name",
                      [], // You can pass any options here if needed
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "ProjectName",
                        tableKey,
                      }
                    )
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.projectName} // Use precomputed class
                  onChange={handleChange}
                  placeholder="Enter Project Name"
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="modelNumber">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("ModelNumber")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Model Number</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="modelNumber"
                  value={formData.modelNumber}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Model Number",
                      [], // You can pass any options here if needed
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "ModelNumber",
                        tableKey,
                      }
                    )
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.modelNumber} // Use precomputed class
                  placeholder="Enter Model Number"
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="salesRepName">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("SalesRep")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Sales Rep Name</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Select
                  name="salesRepName"
                  value={formData.salesRepName}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Sales Rep Name",
                      userDropdowns.salesOptions, // Use sales user dropdown options
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "SalesRep",
                        tableKey,
                      }
                    )
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.Sales)}
                  className={fieldClasses.salesRepName}
                  onChange={handleChange}
                >
                  <option value="">Select Sales Rep</option>
                  {(userDropdowns.salesOptions || []).map((option) => (
                    <option key={option.ID} value={option.displayName}>
                      {option.displayName}
                    </option>
                  ))}
                  {!isValueInDropdown(
                    formData.salesRepName,
                    userDropdowns.salesOptions || []
                  ) &&
                    formData.salesRepName && (
                      <option
                        value={formData.salesRepName}
                        style={{ color: "red" }}
                      >
                        *{formData.salesRepName}
                      </option>
                    )}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>          <Row className="mb-3 row-cols-1 row-cols-sm-2 row-cols-lg-4">
            <Col>
              <Form.Group controlId="zone">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Zones")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Zones</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Select
                  name="zone"
                  value={formData.zone}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(e, "Zone", zoneOptions, {
                      ID: formData.ID,
                      url: apiUrl,
                      field: "Zones",
                      tableKey,
                    })
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.zone} // Use precomputed class
                  onChange={handleChange}
                >
                  <option value="">Select Zone</option>
                  {zoneOptions.map((option) => (
                    <option key={option.ID} value={option.DisplayText}>
                      {option.DisplayText}
                    </option>
                  ))}
                  {!isValueInDropdown(formData.zone, zoneOptions) &&
                    formData.zone && (
                      <option value={formData.zone} style={{ color: "red" }}>
                        *{formData.zone}
                      </option>
                    )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="drawingStatus">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("DrawingStatus")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Drawing Status</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Select
                  name="drawingStatus"
                value= {formData.drawingStatus}
                  onFocus={handleFocus} // Capture the current value on focus
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Drawing Status",
                      drawingStatusOptions, // You can pass any options here if needed
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "DrawingStatus",
                        tableKey,
                      }
                    )
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.drawingStatus} // Use precomputed class
                  onChange={handleChange}
                >
                  <option value="">Select Drawing Status</option>
                  {drawingStatusOptions.map((option) => (
                    <option key={option.ID} value={option.DisplayText}>
                      {option.DisplayText}
                    </option>
                  ))}
                                    {!isValueInDropdown(
                    formData.drawingStatus,
                    drawingStatusOptions
                  ) &&
                    formData.drawingStatus && (
                      <option
                        value={formData.drawingStatus}
                        style={{ color: "red" }}
                      >
                        *{formData.drawingStatus}
                      </option>
                    )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="projectStatus">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("ProjectStatus")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Project Status</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Select
                  name="projectStatus"
                  value={formData.projectStatus}
                  onFocus={handleFocus} // Capture the current value on focus
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Project Status",
                      projectStatusOptions, // You can pass any options here if needed
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "ProjectStatus",
                        tableKey,
                      }
                    )
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.projectStatus} // Use precomputed class
                  onChange={handleChange}
                >
                  <option value="">Select Project Status</option>
                  {projectStatusOptions.map((option) => (
                    <option key={option.ID} value={option.DisplayText}>
                      {option.DisplayText}
                    </option>
                  ))}
                  {!isValueInDropdown(
                    formData.projectStatus,
                    projectStatusOptions
                  ) &&
                    formData.projectStatus && (
                      <option
                        value={formData.projectStatus}
                        style={{ color: "red" }}
                      >
                        *{formData.projectStatus}
                      </option>
                    )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="projectManager">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("ProjectManager")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Project Manager</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Select
                  name="projectManager"
                  value={formData.projectManager}
                  onFocus={handleFocus} // Capture the current value on focus
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Project Manager",
                      userDropdowns.appsOptions, // You can pass any options here if needed
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "ProjectManager",
                        tableKey,
                      }
                    )
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.projectManager} // Use precomputed class
                  onChange={handleChange}
                >
                  <option value="">Select Project Manager</option>
                  {userDropdowns.appsOptions.map((option) => (
                    <option key={option.ID} value={option.displayName}>
                      {option.displayName}
                    </option>
                  ))}
                  {!isValueInDropdown(
                    formData.projectManager,
                    userDropdowns.appsOptions
                  ) &&
                    formData.projectManager && (
                      <option
                        value={formData.projectManager}
                        style={{ color: "red" }}
                      >
                        *{formData.projectManager}
                      </option>
                    )}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3 row-cols-1 row-cols-sm-3 row-cols-lg-5">
            <Col>
              <Form.Group controlId="orderDate">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("OrderDate")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Order Date</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="orderDate"
                  value={
                    formData.orderDate ? formData.orderDate.split("T")[0] : ""
                  }
                  onChange={handleChange}
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.orderDate} // Use precomputed class
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Order Date",
                      [], // You can pass any options here if needed
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "OrderDate",
                        tableKey,
                      }
                    )
                  }
                />
              </Form.Group>
            </Col>

            <Col>
              <Form.Group controlId="requestedDeliveryDate">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("RequestedDeliveryDate")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Requested Delivery Date</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="requestedDeliveryDate"
                  value={
                    formData.requestedDeliveryDate
                      ? formData.requestedDeliveryDate.split("T")[0]
                      : ""
                  }
                  onChange={handleChange}
                  onFocus={handleFocus}
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.requestedDeliveryDate} // Use precomputed class
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Requested Delivery Date",
                      [], // You can pass any options here if needed
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "RequestedDeliveryDate",
                        tableKey,
                      }
                    )
                  }
                />
              </Form.Group>
            </Col>

            <Col>
              <Form.Group controlId="csoDueDate">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("CSODueDate")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>CSO Due Date</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="csoDueDate"
                  value={
                    formData.csoDueDate ? formData.csoDueDate.split("T")[0] : ""
                  }
                  onChange={handleChange}
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.csoDueDate} // Use precomputed class
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "CSO Due Date",
                      [], // You can pass any options here if needed
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "CSODueDate",
                        tableKey,
                      }
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="releaseDate">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("ReleaseDate")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Release Meeting Date</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="releaseDate"
                  value={
                    formData.releaseDate
                      ? formData.releaseDate.split("T")[0]
                      : ""
                  }
                  onChange={handleChange}
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.releaseDate} // Use precomputed class
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Release Meeting Date",
                      [], // You can pass any options here if needed
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "ReleaseDate",
                        tableKey,
                      }
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="dateShipped">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("ActualShipDate")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Date Shipped</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="dateShipped"
                  value={
                    formData.dateShipped
                      ? formData.dateShipped.split("T")[0]
                      : ""
                  }
                  onChange={handleChange}
                  onFocus={handleFocus}
                  disabled={true}
                  className={fieldClasses.dateShipped} // Use precomputed class
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Date Shipped",
                      [], // You can pass any options here if needed
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "ActualShipDate",
                        tableKey,
                      }
                    )
                  }
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3 mb-3 row-cols-1 row-cols-sm-2 row-cols-lg-5">
            <Col>
              <Form.Group controlId="mechanicalEngineer">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("MechanicalEngineer")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Mechanical Engineer</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Select
                  name="mechanicalEngineer"
                  value={formData.mechanicalEngineer}
                  onFocus={handleFocus} // Capture the current value on focus
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Mechanical Engineer",
                      userDropdowns.mechOptions, // You can pass any options here if needed
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "MechanicalEngineer",
                        tableKey,
                      }
                    )
                  }
                  disabled={
                    isDisabled ||
                    (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)
                  }
                  className={fieldClasses.mechanicalEngineer} // Use precomputed class
                  onChange={handleChange}
                >
                  <option value="">Select Mechanical Engineer</option>
                  {userDropdowns.mechOptions.map((option) => (
                    <option key={option.ID} value={option.displayName}>
                      {option.displayName}
                    </option>
                  ))}
                  {!isValueInDropdown(
                    formData.mechanicalEngineer,
                    userDropdowns.mechOptions
                  ) &&
                    formData.mechanicalEngineer && (
                      <option
                        value={formData.mechanicalEngineer}
                        style={{ color: "red" }}
                      >
                        *{formData.mechanicalEngineer}
                      </option>
                    )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="electricalEngineer">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("ElectricalEngineer")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Electrical Engineer</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Select
                  name="electricalEngineer"
                  value={formData.electricalEngineer}
                  onFocus={handleFocus} // Capture the current value on focus
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Electrical Engineer",
                      userDropdowns.elecOptions, // You can pass any options here if needed
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "ElectricalEngineer",
                        tableKey,
                      }
                    )
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.ElectricalEngineer)}
                  className={fieldClasses.electricalEngineer} // Use precomputed class
                  onChange={handleChange}
                >
                  <option value="">Select Electrical Engineer</option>
                  {userDropdowns.elecOptions.map((option) => (
                    <option key={option.ID} value={option.displayName}>
                      {option.displayName}
                    </option>
                  ))}
                  {!isValueInDropdown(
                    formData.electricalEngineer,
                    userDropdowns.elecOptions
                  ) &&
                    formData.electricalEngineer && (
                      <option
                        value={formData.electricalEngineer}
                        style={{ color: "red" }}
                      >
                        *{formData.electricalEngineer}
                      </option>
                    )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="controlsEngineer">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("ControlsEngineer")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Controls Engineer</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Select
                  name="controlsEngineer"
                  value={formData.controlsEngineer}
                  onFocus={handleFocus} // Capture the current value on focus
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Controls Engineer",
                      userDropdowns.controlOptions, // You can pass any options here if needed
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "ControlsEngineer",
                        tableKey,
                      }
                    )
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.ControlEngineer)}
                  className={fieldClasses.controlsEngineer} // Use precomputed class
                  onChange={handleChange}
                >
                  <option value="">Select Controls Engineer</option>
                  {userDropdowns.controlOptions.map((option) => (
                    <option key={option.ID} value={option.displayName}>
                      {option.displayName}
                    </option>
                  ))}
                  {!isValueInDropdown(
                    formData.controlsEngineer,
                    userDropdowns.controlOptions
                  ) &&
                    formData.controlsEngineer && (
                      <option
                        value={formData.controlsEngineer}
                        style={{ color: "red" }}
                      >
                        *{formData.controlsEngineer}
                      </option>
                    )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="customControlsEngineering">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("CustomControlsEngineering")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Custom Controls Engineering</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Select
                  name="customControlsEngineering"
                  value={formData.customControlsEngineering}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Custom Controls Engineering",
                      [], // Pass any options here if needed
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "CustomControlsEngineering",
                        tableKey,
                      }
                    )
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.customControlsEngineering} // Use precomputed class
                  onChange={handleChange}
                >
                  <option value="">Select Option</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          
          <Row className="mb-3 mt-3"> 

            <Col>
              <Form.Group controlId="sendDocumentsToSales">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("SendDocumentsToSales")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Send Production Prints to Sales</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Select
                  name="sendDocumentsToSales"
                  value={formData.sendDocumentsToSales}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Send Documents to Sales",
                      [], // Pass any options here if needed
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "SendDocumentsToSales",
                        tableKey,
                      }
                    )
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.sendDocumentsToSales} // Use precomputed class
                  onChange={handleChange}
                >
                  <option value="">Select Option</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="newConstruction">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("NewConstruction")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>New Construction</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Select
                  name="newConstruction"
                  value={formData.newConstruction}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "New Construction",
                      [], // Pass any options here if needed
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "NewConstruction",
                        tableKey,
                      }
                    )
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.newConstruction} // Use precomputed class
                  onChange={handleChange}
                >
                  <option value="">Select Option</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="hasSubmittal">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("HasSubmittal")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Has Submittal</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Select
                  name="hasSubmittal"
                  value={formData.hasSubmittal}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Has Submittal",
                      [], // Pass any options here if needed
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "HasSubmittal",
                        tableKey,
                      }
                    )
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.hasSubmittal} // Use precomputed class
                  onChange={handleChange}
                >
                  <option value="">Select Option</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Form.Select>
              </Form.Group>
            </Col>           
            <Col>
              <Form.Group controlId="drawingType">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("DrawingType")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>CSO Drawing Type</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Select
                  name="drawingType"
                  value={formData.drawingType}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Drawing Type",
                      drawingTypeOptions, // Pass the drawing type options
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "DrawingType",
                        tableKey,
                      }
                    )
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.drawingType} // Use precomputed class
                  onChange={handleChange}
                >
                  <option value="">Select Drawing Type</option>
                  {drawingTypeOptions.map((option) => (
                    <option key={option.ID} value={option.DisplayText}>
                      {option.DisplayText}
                    </option>
                  ))}
                  {!isValueInDropdown(formData.drawingType, drawingTypeOptions) &&
                    formData.drawingType && (
                      <option value={formData.drawingType} style={{ color: "red" }}>
                        *{formData.drawingType}
                      </option>
                    )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="drawingNumber">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("DrawingNumber")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>CSO Drawing Number</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="drawingNumber"
                  value={formData.drawingNumber}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Drawing Number",
                      [], // You can pass any options here if needed
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "DrawingNumber",
                        tableKey,
                      }
                    )
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.drawingNumber} // Use precomputed class
                  placeholder="Enter Drawing Number"
                />
              </Form.Group>
            </Col>            
            
            
          </Row>

          <Row className="mb-3">
  <Col>
    <Form.Group controlId="releaseMeetingRequired">
      <Form.Label>
        <OverlayTrigger
          placement="top"
          overlay={renderTooltip("ReleaseMeetingRequired")}
          delay={{ show: 300, hide: 200 }}
        >
          {(roles.Admin || roles.Apps) ? (
            <a href={releaseGuidelinesUrl} target="_blank" rel="noopener noreferrer">
              Release Meeting Required
            </a>
          ) : (
            <span>Release Meeting Required</span>
          )}
        </OverlayTrigger>
      </Form.Label>
      <Form.Select
        name="releaseMeetingRequired"
        value={formData.releaseMeetingRequired}
        onFocus={handleFocus}
        onBlur={(e) =>
          handleBlur(
            e,
            "Release Meeting Required",
            genericOptions, // Use generic options
            {
              ID: formData.ID,
              url: apiUrl,
              field: "ReleaseMeetingRequired",
              tableKey,
            }
          )
        }
        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
        className={fieldClasses.releaseMeetingRequired} // Use precomputed class
        onChange={handleChange}
      >
        <option value="">Select Option</option>
        {genericOptions.map((option) => (
          <option key={option.ID} value={option.DisplayText}>
            {option.DisplayText}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  </Col>
  <Col>
    <Form.Group controlId="witnessTestRequired">
      <Form.Label>
        <OverlayTrigger
          placement="top"
          overlay={renderTooltip("WitnessTestRequired")}
          delay={{ show: 300, hide: 200 }}
        >
          <span>Witness Test Required</span>
        </OverlayTrigger>
      </Form.Label>
      <Form.Select
        name="witnessTestRequired"
        value={formData.witnessTestRequired}
        onFocus={handleFocus}
        onBlur={(e) =>
          handleBlur(
            e,
            "Witness Test Required",
            genericOptions, // Use generic options
            {
              ID: formData.ID,
              url: apiUrl,
              field: "WitnessTestRequired",
              tableKey,
            }
          )
        }
        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
        className={fieldClasses.witnessTestRequired} // Use precomputed class
        onChange={handleChange}
      >
        <option value="">Select Option</option>
        {genericOptions.map((option) => (
          <option key={option.ID} value={option.DisplayText}>
            {option.DisplayText}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  </Col>
  {formData.witnessTestRequired === "Yes" && (
    <>
      <Col>
        <Form.Group controlId="testCriteria">
          <Form.Label>
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip("TestCriteria")}
              delay={{ show: 300, hide: 200 }}
            >
              <span>Test Criteria</span>
            </OverlayTrigger>
          </Form.Label>
          <Form.Select
            name="testCriteria"
            value={formData.testCriteria}
            onFocus={handleFocus}
            onBlur={(e) =>
              handleBlur(
                e,
                "Test Criteria",
                options.filter((option) => option.DropdownType === "Test Criteria"), // Pass the test criteria options
                {
                  ID: formData.ID,
                  url: apiUrl,
                  field: "TestCriteria",
                  tableKey,
                }
              )
            }
            disabled={isDisabled || (!roles.Admin && !roles.Apps)}
            className={fieldClasses.testCriteria} // Use precomputed class
            onChange={handleChange}
          >
            <option value="">Select Test Criteria</option>
            {options
              .filter((option) => option.DropdownType === "Test Criteria")
              .map((option) => (
                <option key={option.ID} value={option.DisplayText}>
                  {option.DisplayText}
                </option>
              ))}
            {!isValueInDropdown(
              formData.testCriteria,
              options.filter((option) => option.DropdownType === "Test Criteria")
            ) &&
              formData.testCriteria && (
                <option value={formData.testCriteria} style={{ color: "red" }}>
                  *{formData.testCriteria}
                </option>
              )}
          </Form.Select>
        </Form.Group>
      </Col>
      <Col>
        <Form.Group controlId="complianceCertificateRequired">
          <Form.Label>
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip("ComplianceCertificateRequired")}
              delay={{ show: 300, hide: 200 }}
            >
              <span>Compliance Certificate Required</span>
            </OverlayTrigger>
          </Form.Label>
          <Form.Select
            name="complianceCertificateRequired"
            value={formData.complianceCertificateRequired}
            onFocus={handleFocus}
            onBlur={(e) =>
              handleBlur(
                e,
                "Compliance Certificate Required",
                genericOptions, // Use generic options
                {
                  ID: formData.ID,
                  url: apiUrl,
                  field: "ComplianceCertificateRequired",
                  tableKey,
                }
              )
            }
            disabled={isDisabled || (!roles.Admin && !roles.Apps)}
            className={fieldClasses.complianceCertificateRequired} // Use precomputed class
            onChange={handleChange}
          >
            <option value="">Select Option</option>
            {genericOptions.map((option) => (
              <option key={option.ID} value={option.DisplayText}>
                {option.DisplayText}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>
    </>
  )}
</Row>
{formData.witnessTestRequired === "Yes" && (
  <Row className="general-form-full-width mb-3">
    <Col>
      <Form.Group controlId="witnessTestComments">
        <Form.Label>
          <OverlayTrigger
            placement="top"
            overlay={renderTooltip("WitnessTestComments")}
            delay={{ show: 300, hide: 200 }}
          >
            <span>Witness Test Comments</span>
          </OverlayTrigger>
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={4}
          name="witnessTestComments"
          value={formData.witnessTestComments || ""}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={(e) =>
            handleBlur(
              e,
              "Witness Test Comments",
              [], // Pass any options here if needed
              {
                ID: formData.ID,
                url: apiUrl,
                field: "WitnessTestComments",
                tableKey,
              }
            )
          }
          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
          className={fieldClasses.witnessTestComments} // Use precomputed class
          placeholder="Enter Witness Test Comments"
        />
      </Form.Group>
    </Col>
  </Row>
)}

        </Form>
      </div>
      {/* )} */}
    </div>
  );
}

export default JobGeneralProjectInfoRF;
